import { Check } from "lucide-react";
import { useSEO } from "@/components/SEO";
import { Eyebrow, Masked, Reveal, ArrowLink, PrimaryCTA, GhostCTA, ExternalCTA, Marquee } from "@/components/bits";
import ProfitEngine from "@/components/ProfitEngine";
import Leakage from "@/components/Leakage";
import ProfitLevers from "@/components/ProfitLevers";
import Bento from "@/components/Bento";
import XRayScanner from "@/components/XRayScanner";
import ExecDashboard from "@/components/ExecDashboard";
import CTASection from "@/components/CTASection";
import { SEO, OUTCOMES, INDUSTRIES, IMAGES } from "@/lib/site";

const CHAIN = ["Diagnose", "Prioritize", "Deploy", "Operate", "Measure", "Scale"];

const Home = () => {
    useSEO({ title: SEO["/"].title, description: SEO["/"].description, path: "/" });

    return (
        <>
            {/* HERO */}
            <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-24" data-testid="home-hero">
                <div className="bg-grid-light absolute inset-0 [mask-image:radial-gradient(70%_60%_at_50%_20%,black,transparent)]" aria-hidden="true" />
                <div className="container-x relative grid items-center gap-16 lg:grid-cols-[1.02fr_0.98fr]">
                    <div>
                        <Eyebrow>The Revenue Operating System</Eyebrow>
                        <Masked
                            lines={["Build a More Profitable", "Business.", "Not Another Campaign."]}
                            className="mt-7 font-display text-[42px] font-extrabold leading-[1.04] tracking-tightest sm:text-6xl lg:text-[72px] xl:text-[80px]"
                        />
                        <Masked
                            as="p"
                            delay={0.35}
                            lines={["Grow revenue without growing cost at the same rate."]}
                            className="mt-6 max-w-xl font-display text-lg font-bold text-vantera sm:text-xl"
                        />
                        <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-slategray sm:text-lg">
                            Vantera operates as an outsourced AI-powered revenue growth and business optimization department. We connect marketing, sales, CRM, customer data, revenue recovery, analytics, margin improvement, and cost reduction into one coordinated system — finding where revenue is leaking, where money is being wasted, and where your existing business can produce more profit.
                        </p>
                        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
                            <PrimaryCTA to="/contact" testid="hero-cta-request-xray">Request a Business X-Ray</PrimaryCTA>
                            <div className="flex flex-wrap items-center gap-3">
                                <ExternalCTA to="https://veluro.ai/vanterademo" testid="hero-demo-button">Demo</ExternalCTA>
                                <GhostCTA to="/how-it-works" testid="hero-cta-how-it-works">See How It Works</GhostCTA>
                            </div>
                        </div>
                    </div>
                    <ProfitEngine />
                </div>
            </section>

            <Marquee items={["Grow New Revenue", "Recover Existing Revenue", "Reduce Cost", "More Profit"]} />

            {/* HIDDEN PROFIT PROBLEM */}
            <section className="relative overflow-hidden bg-ink py-24 text-ivory sm:py-32" data-testid="home-problem">
                <div className="container-x grid items-center gap-16 lg:grid-cols-2">
                    <div>
                        <Eyebrow dark>01 — The Hidden Profit Problem</Eyebrow>
                        <Masked
                            inview
                            lines={["Most Established Companies Have More Revenue —", "and More Savings — Hiding", "Inside the Business."]}
                            className="mt-7 max-w-2xl font-display text-3xl font-extrabold leading-[1.12] tracking-tightest sm:text-5xl"
                        />
                        <Reveal delay={0.2}>
                            <p className="mt-7 max-w-xl text-lg leading-relaxed text-cloud">
                                Growth becomes expensive when every new dollar requires more people, more advertising, and more complexity. At the same time, companies often overpay for recurring business expenses without realizing better pricing or vendor options are available.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="mt-10 rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid="home-manifesto">
                                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-cloud">The old equation</p>
                                <p className="mt-3 font-display text-lg font-bold leading-relaxed text-ivory/70 sm:text-xl">
                                    More people + more ads + more software = <span className="text-rose-300">more cost</span>
                                </p>
                                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.22em] text-cloud">The Vantera question</p>
                                <p className="mt-3 font-display text-xl font-extrabold text-vantera sm:text-2xl">
                                    What if the next profit opportunity is already inside the business?
                                </p>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <Leakage />
                    </Reveal>
                </div>
            </section>

            {/* PROFIT LEVERS */}
            <section className="py-24 sm:py-32" data-testid="home-levers">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <Eyebrow>02 — What Vantera Does</Eyebrow>
                        <Masked inview lines={["One System. Three Profit Levers."]} className="mt-7 font-display text-4xl font-extrabold tracking-tightest sm:text-5xl lg:text-6xl" />
                    </div>
                    <div className="mt-14">
                        <ProfitLevers />
                    </div>
                </div>
            </section>

            {/* WHAT VANTERA SEES — BENTO */}
            <section className="border-y border-ink/10 bg-paper py-24 sm:py-32" data-testid="home-bento">
                <div className="container-x">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div className="max-w-2xl">
                            <Eyebrow>03 — What Vantera Sees</Eyebrow>
                            <Masked inview lines={["See the Business Differently."]} className="mt-7 font-display text-4xl font-extrabold tracking-tightest sm:text-5xl" />
                        </div>
                        <p className="max-w-sm text-[16px] leading-relaxed text-slategray">Find what's hiding inside the business you already have — the opportunities others miss.</p>
                    </div>
                    <div className="mt-12">
                        <Bento />
                    </div>
                </div>
            </section>

            {/* BUSINESS X-RAY TEASER */}
            <section className="relative overflow-hidden bg-ink py-24 text-ivory sm:py-32" data-testid="home-xray">
                <div className="container-x grid items-center gap-16 lg:grid-cols-2">
                    <div>
                        <Eyebrow dark>04 — The Business X-Ray</Eyebrow>
                        <Masked
                            inview
                            lines={["Before You Spend More to Grow,", "Find Out What Your Business", "Is Already Losing —", "and Overpaying For."]}
                            className="mt-7 font-display text-3xl font-extrabold leading-[1.12] tracking-tightest sm:text-5xl"
                        />
                        <Reveal delay={0.2}>
                            <p className="mt-7 max-w-xl text-lg leading-relaxed text-cloud">
                                A diagnostic examination of the company's revenue engine and operating economics. Leadership receives a prioritized view of where revenue can be created or recovered, where costs may be reduced, and where margin can improve.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3} className="mt-9">
                            <ArrowLink to="/business-x-ray" dark testid="home-xray-link">Explore the Business X-Ray</ArrowLink>
                        </Reveal>
                    </div>
                    <XRayScanner />
                </div>
            </section>

            {/* EXECUTIVE DASHBOARD */}
            <section className="py-24 sm:py-32" data-testid="home-dashboard">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <Eyebrow>05 — Executive Intelligence</Eyebrow>
                        <Masked inview lines={["Intelligence Leadership Can Actually Operate On."]} className="mt-7 font-display text-4xl font-extrabold tracking-tightest sm:text-5xl" />
                    </div>
                    <div className="mt-12 rounded-2xl bg-ink p-3 sm:p-6">
                        <ExecDashboard />
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS STRIP */}
            <section className="border-y border-ink/10 bg-paper py-20 sm:py-28" data-testid="home-how-strip">
                <div className="container-x grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
                    <div>
                        <Eyebrow>06 — How It Works</Eyebrow>
                        <Masked inview lines={["Turn the Business You Already Have", "Into a More Powerful Profit Engine."]} className="mt-7 font-display text-3xl font-extrabold tracking-tightest sm:text-5xl" />
                        <Reveal delay={0.2}>
                            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slategray">
                                Every engagement begins with the Business X-Ray. We establish the economic baseline, identify the highest-value revenue and savings opportunities, deploy the right workflows and partners, and measure impact against business outcomes.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3} className="mt-8">
                            <ArrowLink to="/how-it-works" testid="home-how-link">See the full methodology</ArrowLink>
                        </Reveal>
                    </div>
                    <div className="flex flex-wrap gap-2.5 lg:justify-end" data-testid="home-method-chain">
                        {CHAIN.map((c, i) => (
                            <Reveal key={c} delay={i * 0.08}>
                                <span className="inline-flex items-center gap-3 rounded-full border border-ink/15 bg-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink">
                                    {c} <span className="text-vantera">→</span>
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* EXECUTIVE OUTCOMES */}
            <section className="py-20 sm:py-28" data-testid="home-outcomes">
                <div className="container-x">
                    <Eyebrow>07 — Executive Outcomes</Eyebrow>
                    <div className="mt-10 grid gap-x-10 gap-y-4 sm:grid-cols-2">
                        {OUTCOMES.map((o, i) => (
                            <Reveal key={o} delay={i * 0.05}>
                                <p className="flex items-center gap-4 border-b border-ink/10 pb-4 font-display text-lg font-bold tracking-tight sm:text-xl">
                                    <span className="font-mono text-[11px] text-vantera">{String(i + 1).padStart(2, "0")}</span>
                                    {o}
                                </p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHO WE HELP TEASER */}
            <section className="pb-24 sm:pb-32" data-testid="home-who">
                <div className="container-x grid items-center gap-14 lg:grid-cols-2">
                    <Reveal className="relative">
                        <div className="overflow-hidden rounded-2xl">
                            <img src={IMAGES.whoWeHelp} alt="Modern industrial warehouse distribution center aisle" className="h-[420px] w-full object-cover" loading="lazy" />
                        </div>
                        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Industrial distribution — a representative Vantera environment</p>
                    </Reveal>
                    <div>
                        <Eyebrow>08 — Who We Help</Eyebrow>
                        <Masked inview lines={["Built for Companies That", "Already Have Momentum."]} className="mt-7 font-display text-3xl font-extrabold tracking-tightest sm:text-5xl" />
                        <Reveal delay={0.15}>
                            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slategray">
                                Established B2B companies with customers, sales infrastructure, CRM data, meaningful transaction values, and the capacity to fulfill additional demand.
                            </p>
                        </Reveal>
                        <div className="mt-8 flex flex-wrap gap-2.5">
                            {INDUSTRIES.map((ind, i) => (
                                <Reveal key={ind.name} delay={i * 0.06}>
                                    <span className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-[13px] font-medium text-ink">
                                        <Check size={12} strokeWidth={3} className="text-vantera" aria-hidden="true" /> {ind.name}
                                    </span>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.25} className="mt-9">
                            <ArrowLink to="/who-we-help" testid="home-who-link">See if Vantera fits your organization</ArrowLink>
                        </Reveal>
                    </div>
                </div>
            </section>

            <CTASection />
        </>
    );
};

export default Home;